import * as React from "react"
import type { Matcher } from "react-day-picker"

import { cn } from "../../../lib/utils"
import { Button } from "../button/button"
import { Calendar } from "../calendar/calendar"
import { Icon, type IconName, type IconSemanticColor } from "../icon/icon"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"
import { Separator } from "../separator/separator"

const DISPLAY_FORMAT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
})

function formatDay(date: Date): string {
  return DISPLAY_FORMAT.format(date)
}

function startOfLocalDay(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

export type DatePickerLabels = {
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string
  /** Accessible label for the trigger button. */
  triggerAria?: string
  clear?: string
  today?: string
}

export type DatePickerProps = {
  value?: Date
  onChange: (date: Date | undefined) => void
  /** Visible trigger text. The selected date remains the accessible label. */
  displayLabel?: string
  /** Disable selecting days before this date. */
  fromDate?: Date
  /** Disable selecting days after this date. */
  toDate?: Date
  /** Caption navigation style. Defaults to free month + year dropdowns. */
  captionLayout?: "label" | "dropdown" | "dropdown-months" | "dropdown-years"
  /** Earliest navigable month. Defaults to 10 years before today. */
  startMonth?: Date
  /** Latest navigable month. Defaults to 10 years after today. */
  endMonth?: Date
  disabled?: boolean
  align?: "start" | "center" | "end"
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
  id?: string
  triggerClassName?: string
  /** Leading glyph on the trigger. Defaults to the calendar. */
  triggerIcon?: IconName
  triggerIconColor?: IconSemanticColor
  labels?: DatePickerLabels
  "aria-invalid"?: boolean
  "aria-describedby"?: string
  "aria-required"?: boolean
}

function DatePicker({
  value,
  onChange,
  displayLabel,
  fromDate,
  toDate,
  captionLayout = "dropdown",
  startMonth,
  endMonth,
  disabled,
  align = "start",
  weekStartsOn,
  id,
  triggerClassName,
  triggerIcon = "calendar",
  triggerIconColor = "muted",
  labels,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [visibleMonth, setVisibleMonth] = React.useState<Date>(value ?? startOfLocalDay())

  React.useEffect(() => {
    if (value) setVisibleMonth(value)
  }, [value])

  const disabledMatcher = React.useMemo<Matcher[] | undefined>(() => {
    const matchers: Matcher[] = []
    if (fromDate) matchers.push({ before: fromDate })
    if (toDate) matchers.push({ after: toDate })
    return matchers.length > 0 ? matchers : undefined
  }, [fromDate, toDate])

  const resolvedStartMonth = React.useMemo(() => {
    if (startMonth) return startMonth
    if (fromDate) return fromDate
    const today = new Date()
    return new Date(today.getFullYear() - 10, 0, 1)
  }, [startMonth, fromDate])

  const resolvedEndMonth = React.useMemo(() => {
    if (endMonth) return endMonth
    if (toDate) return toDate
    const today = new Date()
    return new Date(today.getFullYear() + 10, 11, 31)
  }, [endMonth, toDate])

  const hasValue = Boolean(value)
  const triggerLabel = hasValue && value ? formatDay(value) : (labels?.placeholder ?? "Pick a date")
  const visibleTriggerLabel = displayLabel ?? triggerLabel

  function isBeforeMinDate(date: Date): boolean {
    if (!fromDate) return false
    return startOfLocalDay(date) < startOfLocalDay(fromDate)
  }

  function handleSelect(date: Date | undefined) {
    if (date && isBeforeMinDate(date)) return
    onChange(date)
    if (date) setOpen(false)
  }

  function handleClear() {
    onChange(undefined)
    setOpen(false)
  }

  function handleToday() {
    const today = startOfLocalDay()
    if (isBeforeMinDate(today)) return
    onChange(today)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-label={labels?.triggerAria ?? (displayLabel !== undefined ? triggerLabel : undefined)}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          aria-required={ariaRequired}
          data-empty={!hasValue}
          className={cn(
            "flex h-9 w-full cursor-pointer items-center gap-2 rounded-sm border border-input-border bg-input-background px-3 text-left text-sm outline-none transition-colors duration-200",
            "hover:border-input-focus-border focus-visible:border-input-focus-border focus-visible:ring-2 focus-visible:ring-ring/40",
            "aria-invalid:border-error aria-invalid:ring-[1px] aria-invalid:ring-error/20",
            "disabled:cursor-not-allowed disabled:opacity-40",
            triggerClassName,
          )}
        >
          <Icon
            name={triggerIcon}
            size="sm"
            color={triggerIconColor}
            className="shrink-0"
          />
          <span className={cn("min-w-0 flex-1 truncate", !hasValue && "text-muted-foreground")}>
            {visibleTriggerLabel}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto bg-muted p-0" sideOffset={4}>
        <Calendar
          mode="single"
          autoFocus
          captionLayout={captionLayout}
          month={visibleMonth}
          onMonthChange={setVisibleMonth}
          startMonth={resolvedStartMonth}
          endMonth={resolvedEndMonth}
          selected={value}
          onSelect={handleSelect}
          weekStartsOn={weekStartsOn}
          disabled={disabledMatcher}
        />
        <Separator />
        <div className="flex items-center justify-between gap-2 p-2.5">
          <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={!hasValue}>
            {labels?.clear ?? "Clear"}
          </Button>
          <Button type="button" size="sm" onClick={handleToday}>
            {labels?.today ?? "Today"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DatePicker }
